import React from 'react'
import { Button, Confirm, Form, Message } from 'semantic-ui-react'
import { Formik, FieldArray } from 'formik'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import styled from 'styled-components'
import hash from 'object-hash'

import {
  hasSentimentRule,
  generateNewQuestion,
  generatePreviewLink,
  findGateOrderViolations,
  hasActiveVisibilityRule,
  hasSurveyBeenSaved,
  isMetricQuestion,
} from './utils'
import { AddQuestionButton } from 'components/AddQuestionButton'
import { QuestionBuilderCard } from 'organisms/QuestionBuilderCard'
import { SurveySettingConsumer } from 'pages/SurveySettings/context'
import { surveyHasFlippableQuestion } from 'libs/questions'
import constants from '../../config/constants'
import { AllowPermission } from 'shared/components/AllowPermission'
import { Modal } from 'semantic-ui-react'
import GenerateQuestionContainer from 'organisms/GenerateQuestion/container'
import { toast } from 'libs/toast'
import { TEMP_ID_PREFIX } from './constants'
import useFeatureToggle from 'hooks/useFeatureToggle'
import { QuestionType } from 'packages/question-types'

const FOLLOW_UP_KEYS = [
  'followUpCommentQuestion',
  'followUpFileUploadQuestion',
  'followUpMultiSelectChoiceQuestion',
  'followUpMultipleChoiceQuestion',
]
const NOT_BLOCKED = Symbol('notBlocked')

const getActiveChoiceIds = (question) => {
  const questionChoices = question?.choices || []
  const deletedIds = new Set(
    (question.deletedExistChoices || []).map((choice) => String(choice.id))
  )

  const validValues = new Set()

  questionChoices
    .filter((choice) => !deletedIds.has(String(choice?.id)))
    .forEach((choice) => {
      validValues.add(String(choice.id))

      if (choice.sentiment) {
        validValues.add(choice.sentiment)
      }
    })

  return Array.from(validValues)
}

const getFollowUpQuestions = (questions) =>
  questions
    .flatMap((question) => question.questionCondition?.conditions || [])
    .flatMap((condition) => FOLLOW_UP_KEYS.map((key) => condition[key]))
    .filter((followUpQuestion) => !R.isEmpty(followUpQuestion))

const findSourceQuestion = (mainQuestions, followUpQuestions, sourceQuestionId) =>
  mainQuestions.find(
    (question) => question.id === sourceQuestionId || question.uniqueIdentifier === sourceQuestionId
  ) || followUpQuestions.find((followUpQuestion) => followUpQuestion.id === sourceQuestionId)

const pruneConditionRule = (condition, sourceQuestion) => {
  const ruleIds = condition.rule.map(String)
  const activeChoiceIds = new Set(getActiveChoiceIds(sourceQuestion))
  const validRuleIds = ruleIds.filter(
    (id) => activeChoiceIds.has(id) || id.startsWith(TEMP_ID_PREFIX)
  )

  if (validRuleIds.length !== ruleIds.length) {
    condition.rule = validRuleIds
  }

  return validRuleIds
}

const findBlockingQuestionId = (targetQuestion, mainQuestions, followUpQuestions) => {
  const visibilityConditions = (targetQuestion.visibilityCondition || []).filter(
    hasActiveVisibilityRule
  )

  for (const condition of visibilityConditions) {
    const sourceQuestion = findSourceQuestion(
      mainQuestions,
      followUpQuestions,
      condition.sourceQuestionId
    )

    if (!sourceQuestion) {
      return targetQuestion.id || targetQuestion.uniqueIdentifier
    }

    if (pruneConditionRule(condition, sourceQuestion).length === 0) {
      return targetQuestion.id
    }
  }

  return NOT_BLOCKED
}

class SideEffectHandler extends React.Component {
  static propTypes = {
    saveFunction: PropTypes.func,
    surveySettingContext: PropTypes.shape({
      setContext: PropTypes.func,
    }),
    isFromCreateSurveyModal: PropTypes.bool,
  }

  componentDidMount() {
    const { setContext: setSurveySettingContext } = this.props.surveySettingContext
    setSurveySettingContext({
      currentSaveFunction: this.props.saveFunction,
    })

    if (this.props.isFromCreateSurveyModal) {
      setSurveySettingContext({
        isSurveyBuilderDirty: true,
      })
    }
  }

  componentDidUpdate(prevProps) {
    const { setContext: setSurveySettingContext } = this.props.surveySettingContext
    if (prevProps.saveFunction !== this.props.saveFunction) {
      setSurveySettingContext({
        currentSaveFunction: this.props.saveFunction,
      })
    }
  }

  render() {
    return null
  }
}

class BuilderFlippabilityPublisher extends React.Component {
  static propTypes = {
    questions: PropTypes.array,
    surveySettingContext: PropTypes.shape({
      setContext: PropTypes.func,
      hasFlippableBuilderQuestion: PropTypes.bool,
    }),
  }

  publishIfChanged() {
    const next = surveyHasFlippableQuestion(this.props.questions)
    if (this.props.surveySettingContext.hasFlippableBuilderQuestion !== next) {
      this.props.surveySettingContext.setContext({ hasFlippableBuilderQuestion: next })
    }
  }

  componentDidMount() {
    this.publishIfChanged()
  }

  componentDidUpdate() {
    this.publishIfChanged()
  }

  componentWillUnmount() {
    if (this.props.surveySettingContext.hasFlippableBuilderQuestion !== null) {
      this.props.surveySettingContext.setContext({ hasFlippableBuilderQuestion: null })
    }
  }

  render() {
    return null
  }
}

const AddQuestionWrapper = styled.div`
  text-align: center;
  border-radius: 10px;
  border: solid 1px #cecece;
  background-color: #f9f9f9;
  box-shadow: none;
  position: relative;
  margin: 2rem 0 2.4rem 0;
  padding: 2.2em 1em;
  border-style: dashed;
`

export class SurveySettingForm extends React.Component {
  submitButtonRef = React.createRef()
  skipDirty = false

  state = {
    isOpenConfirmPopup: false,
    isOpenGeneratePopup: false,
    aiQuestions: [],
    questionIdWithVisibilityRuleError: null,
  }
  setAiQuestions = (data) => {
    this.setState({ aiQuestions: data })
  }
  selectedQuestions = () => {
    return this.state.aiQuestions.filter((question) => question.isSelected)
  }
  handleFormKeyPress = (e) => {
    const key = e.charCode || e.keyCode || 0
    const isEnterKey = key === 13
    if (isEnterKey) {
      e.preventDefault()
    }
  }

  saveButtonFunction = () => {
    const form = this.submitButtonRef.current?.form
    if (form && !form.checkValidity()) {
      form.reportValidity()
      return
    }
    this.submitButtonRef.current.click()
  }

  handleQuestionDelete = (surveySettingContext, question, questionIndex, arrayHelpers) => {
    if (!question.id) {
      return
    }

    this.props.sendingDeleteQuestion({
      question,
      questionIndex,
      onFailure: () => {
        arrayHelpers.insert(questionIndex, question)
      },
    })
    this.skipDirty = true
  }

  handleCancelPopup = () => this.setState({ isOpenConfirmPopup: false })
  handleConfirmPopup = (values) => {
    this.props.sendingForm(values)
    this.setState({ isOpenConfirmPopup: false })
  }

  validateVisibilityRules = (values) => {
    const mainQuestions = values?.questions || []
    const followUpQuestions = getFollowUpQuestions(mainQuestions)

    let invalidQuestionId = null

    for (const targetQuestion of mainQuestions) {
      const blockingId = findBlockingQuestionId(targetQuestion, mainQuestions, followUpQuestions)
      if (blockingId === NOT_BLOCKED) {
        continue
      }
      invalidQuestionId = blockingId
      if (invalidQuestionId) {
        break
      }
    }

    this.setState({
      questionIdWithVisibilityRuleError: invalidQuestionId,
    })
    return Boolean(invalidQuestionId)
  }

  onSubmitForm = (values) => {
    this.setState({ questionIdWithVisibilityRuleError: null })

    const shouldWarningResetSentimentRule = values.questions.some((question) => {
      return (
        R.prop('shouldWarningResetSentimentRule', question) &&
        hasSentimentRule(question.questionCondition.conditions)
      )
    })

    const isVisibilityRuleError = this.validateVisibilityRules(values)

    if (isVisibilityRuleError) {
      return toast.error(
        'At least one choice or sentiment needs to be included as a visibility rule before saving.'
      )
    }

    if (shouldWarningResetSentimentRule) {
      this.setState({ isOpenConfirmPopup: true })
    } else {
      this.props.sendingForm(values)
    }
  }

  handleOpenGeneratePopup = () => {
    this.setState({ isOpenGeneratePopup: true })
    this.setState({ aiQuestions: [] })
  }

  getAutoId = (length = 6) => {
    return Math.random().toString(20).substr(2, length)
  }

  handleAddQuestions = (questions, push) => {
    const selectedQuestions = [...questions]

    selectedQuestions.forEach((question) => {
      const constantQuestion = generateNewQuestion(question.type)

      if (
        question.type == QuestionType.MULTIPLE_CHOICE ||
        question.type == QuestionType.MULTI_SELECT_CHOICE
      ) {
        question.choices.forEach((choice) => {
          return (choice.id = TEMP_ID_PREFIX + this.getAutoId())
        })
      }

      push(R.mergeDeepRight(constantQuestion, question))
    })
    this.setState({ isOpenGeneratePopup: false })

    toast.success(`You have successfully added ${selectedQuestions.length} questions.`)
  }

  onremoveWithFeatureToggle = () => {
    if (this.props.isEnableMultilingualSurvey) {
      this.handleOpenGeneratePopup()
    }
  }
  render() {
    return (
      <SurveySettingConsumer>
        {(surveySettingContext) => (
          <Formik
            key={hash(this.props.surveyQuestions)}
            initialValues={this.props.surveyQuestions}
            validate={() => {
              if (this.skipDirty) {
                this.skipDirty = false
                return
              }

              surveySettingContext.setContext({
                isSurveyBuilderDirty: true,
              })
            }}
            onSubmit={(values) => this.onSubmitForm(values)}
            render={({ values, handleSubmit, setFieldValue }) => {
              const { surveyToken, surveyQuestions } = this.props
              const enableMetricReorder = !!this.props.enableMetricReorder
              const gateViolations = enableMetricReorder
                ? findGateOrderViolations(values.questions)
                : []
              const hasGateViolations = gateViolations.length > 0
              return (
                <Form onSubmit={handleSubmit} onKeyPress={this.handleFormKeyPress}>
                  <Confirm
                    header="Confirm rule modification"
                    content={confirmContent}
                    open={this.state.isOpenConfirmPopup}
                    onCancel={this.handleCancelPopup}
                    onConfirm={() => this.handleConfirmPopup(values)}
                    size="tiny"
                  />
                  <SideEffectHandler
                    saveFunction={this.saveButtonFunction}
                    surveySettingContext={surveySettingContext}
                    isFromCreateSurveyModal={this.props.hasTemplateFromLocationState}
                  />
                  <BuilderFlippabilityPublisher
                    questions={values.questions}
                    surveySettingContext={surveySettingContext}
                  />
                  <FieldArray name="questions">
                    {(arrayHelpers) => {
                      const { push, remove, move } = arrayHelpers
                      const canChangePrimaryMetric =
                        !this.props.hasTemplateFromLocationState &&
                        !hasSurveyBeenSaved(values.questions)
                      return (
                        <React.Fragment>
                          {values.questions.map((question, index) => {
                            const isFirstQuestion = index === 0
                            const isFirstNonPrimaryQuestion = index === 1
                            // AC10: with the screener flag on, the primary-branch
                            // render keys on `is_primary_metric`, not position —
                            // after reorder the metric may sit at any index.
                            const isThisMetric = enableMetricReorder
                              ? isMetricQuestion(question)
                              : isFirstQuestion
                            const isLastQuestion = index === values.questions.length - 1

                            return (
                              <QuestionBuilderCard
                                key={question.id || question.tempId}
                                choiceIconSetOptions={this.props.choiceIconSetOptions}
                                arrayHelpers={arrayHelpers}
                                canChangePrimaryMetric={canChangePrimaryMetric}
                                setFieldValue={setFieldValue}
                                primary={isThisMetric}
                                questionIndex={index}
                                question={question}
                                questionResponse={this.props.questionsResponse.questions[index]}
                                questions={values.questions}
                                onRemove={() => {
                                  remove(index)
                                  this.handleQuestionDelete(
                                    surveySettingContext,
                                    values.questions[index],
                                    index,
                                    arrayHelpers
                                  )
                                }}
                                removeVisibilityCondition={(questionIndex) => {
                                  setFieldValue(
                                    `questions[${questionIndex}].visibilityCondition`,
                                    []
                                  )
                                }}
                                disableMoveUp={
                                  enableMetricReorder ? isFirstQuestion : isFirstNonPrimaryQuestion
                                }
                                disableMoveDown={isLastQuestion}
                                onMoveDown={R.partial(move, [index, index + 1])}
                                onMoveUp={R.partial(move, [index, index - 1])}
                                surveyToken={this.props.surveyToken}
                                questionIdWithVisibilityRuleError={
                                  this.state.questionIdWithVisibilityRuleError
                                }
                              />
                            )
                          })}
                          <AddQuestionWrapper>
                            <AddQuestionButton
                              onSelect={(questionType) =>
                                push(generateNewQuestion(questionType, values.questions.length + 1))
                              }
                              handleOpenGeneratePopup={this.handleOpenGeneratePopup}
                            />
                          </AddQuestionWrapper>
                          {hasGateViolations && (
                            <Message
                              negative
                              data-testid="gate-order-violation-banner"
                              header="Fix visibility rule order before saving"
                              content={`One or more questions have a visibility rule that points to a question later in the list (${
                                gateViolations.length
                              } issue${
                                gateViolations.length === 1 ? '' : 's'
                              }). Move each source question above the question it controls.`}
                            />
                          )}
                          <div style={{ textAlign: 'right' }}>
                            <a
                              href={generatePreviewLink(surveyToken, surveyQuestions)}
                              target="_blank"
                              rel="noopener"
                            >
                              <Button type="button" basic data-testid="survey-preview-btn">
                                <i className="fas fa-eye icon" />
                                Preview
                              </Button>
                            </a>
                            <AllowPermission permission={constants.permissions.manageSurveys}>
                              <Button
                                id="survey-save-btn"
                                data-testid="survey-save-btn"
                                primary
                                onClick={this.saveButtonFunction}
                                disabled={this.props.isSubmitting || hasGateViolations}
                                loading={this.props.isSubmitting}
                                type="button"
                              >
                                Save
                              </Button>
                            </AllowPermission>
                          </div>
                          <button hidden ref={this.submitButtonRef} type="submit" />

                          <Modal
                            className="large coupled new-survey first"
                            open={this.state.isOpenGeneratePopup}
                            onClose={() => this.setState({ isOpenGeneratePopup: false })}
                          >
                            <Modal.Header>AI question generator</Modal.Header>
                            <Modal.Content className="!p-0 rounded">
                              <GenerateQuestionContainer
                                surveyToken={this.props.surveyToken}
                                questions={this.state.aiQuestions}
                                setQuestions={this.setAiQuestions}
                              />
                            </Modal.Content>
                            <Modal.Actions>
                              <Button
                                className="primary"
                                disabled={
                                  this.selectedQuestions() && this.selectedQuestions().length < 1
                                }
                                onClick={() => {
                                  this.handleAddQuestions(this.selectedQuestions(), push)
                                }}
                              >
                                Add selected questions
                              </Button>
                            </Modal.Actions>
                          </Modal>
                        </React.Fragment>
                      )
                    }}
                  </FieldArray>
                </Form>
              )
            }}
          />
        )}
      </SurveySettingConsumer>
    )
  }
}

SurveySettingForm.propTypes = {
  sendingForm: PropTypes.func,
  sendingDeleteQuestion: PropTypes.func,
  surveyQuestions: PropTypes.object,
  questionsResponse: PropTypes.object,
  hasTemplateFromLocationState: PropTypes.bool,
  surveyToken: PropTypes.string,
  isSubmitting: PropTypes.bool,
  choiceIconSetOptions: PropTypes.array,
  isEnableMultilingualSurvey: PropTypes.bool,
  enableMetricReorder: PropTypes.bool,
}

// PET-1965: functional wrapper that supplies the `enable_metric_reorder`
// flag to the class component without touching its render-signature.
// eslint-disable-next-line react/display-name
export const SurveySettingFormWithMetricReorderFlag = (props) => {
  const { enableMetricReorder } = useFeatureToggle()
  return <SurveySettingForm {...props} enableMetricReorder={enableMetricReorder} />
}

const confirmContent =
  'You are about to edit a question that includes at least one sentiment-based rule. Confirming this change will remove those associated actions. To retain these actions, cancel and adjust prior to editing.'



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/SurveySettingsV2/SurveySettingForm.js